import React from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useCurrentTalentProfile } from "../hooks";
import { useGetUserProfile } from "../services/api/user.api";
import { useErrorResponse } from "../hooks/useErrorResponse";
import { AppUserSession } from "../services/user-session";
import ScreenLoader from "../components/Loader/ScreenLoader";
import { validateToken } from "../utils";
import usebusinessInfo from "../hooks/useBusiness";
import { useGetBusinessProfile } from "../services/api/business/business.api";
import { User } from "@/types/user";
import useCurrentGTP from "../hooks/useGTP";
const userRoutePattern = "user/:id";

// Type definition for WrappedComponent's props
interface WithAuthenticationProps {
  children?: React.ReactNode;
}

const withAuthentication = (
  WrappedComponent: React.ComponentType<WithAuthenticationProps>,
  allowedRoutes: string[] = []
) => {
  return (props: WithAuthenticationProps) => {
    const { userInfo, setUser } = useAuth();
    const { currentGTP } = useCurrentGTP();
    const { currentProfile } = useCurrentTalentProfile();
    const accessToken = AppUserSession.getToken();
    const [userLoading, setUserLoading] = React.useState(false);
    const [shouldRender, setShouldRender] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { businessInfo, setBusinessInfo } = usebusinessInfo();
    const [businessLoading, setBusinessLoading] = React.useState(false);

    const {
      refetch,
      data: user,
      error,
    } = useGetUserProfile({
      key: ["user-profile"],
      options: { enabled: false },
    });

    const {
      refetch: businessRefetch,
      data: businessProfile,
      error: businessError,
    } = useGetBusinessProfile({
      key: ["business-profile-auth"],
      options: { enabled: false },
    });

    if (accessToken && !userInfo && !userLoading) {
      const isTokenValid = validateToken(accessToken);

      if (!isTokenValid) {
        localStorage.clear();
      } else {
        setUserLoading(true);
        // setBusinessLoading(true);
        refetch();
      }
    }
    React.useEffect(() => {
      if (user?.data) {
        setUser(user?.data as User);
        setUserLoading(false);
      }
      // if (user?.data?.account_type === "Business") {
      //   setbusinessInfo(user.data as Business);
      // }
    }, [user, setUser]);

    useErrorResponse(error);

    React.useEffect(() => {
      if (user?.data?.account_type === "Business" && !businessProfile) {
        setBusinessLoading(true);
        businessRefetch();
      }
    }, [user?.data?.account_type, businessProfile, businessRefetch]);

    React.useEffect(() => {
      if (businessProfile?.data) {
        setBusinessInfo(businessProfile.data);
        setBusinessLoading(false);
      }
    }, [businessProfile]);

    useErrorResponse(businessError);

    // useErrorResponse(businessError);
    // const isLoggedIn = !!accessToken && (!!userInfo || !!businessInfo);

    const isLoggedIn = !!accessToken && !!userInfo;

    // Handle redirects and determine if we should render
    React.useEffect(() => {
      if (userLoading) return;

      const currentPath = window.location.pathname;
      const userMatchedRoutePattern = matchPath(userRoutePattern, currentPath);

      if (isLoggedIn) {
        // Check for stored fanCard redirect URL first - this should take priority
        const storedRedirectUrl = localStorage.getItem("fanCardRedirectUrl");

        if (storedRedirectUrl) {
          console.log(
            "withAuthentication: Redirecting to stored fanCard URL:",
            storedRedirectUrl,
            "User info:",
            userInfo,
            "Access token:",
            !!accessToken
          );
          localStorage.removeItem("fanCardRedirectUrl");
          // Use a small delay to ensure the user is fully authenticated
          setTimeout(() => {
            console.log(
              "withAuthentication: Executing redirect to:",
              storedRedirectUrl
            );
            window.location.href = storedRedirectUrl;
          }, 100);
          setShouldRender(false);
          return;
        }

        // Check for stored shared booking redirect URL
        const sharedBookingRedirect = localStorage.getItem(
          "sharedBookingRedirect"
        );
        if (sharedBookingRedirect) {
          console.log(
            "withAuthentication: Redirecting to stored shared booking URL:",
            sharedBookingRedirect
          );
          localStorage.removeItem("sharedBookingRedirect");
          // Use a small delay to ensure the user is fully authenticated
          setTimeout(() => {
            console.log(
              "withAuthentication: Executing redirect to shared booking:",
              sharedBookingRedirect
            );
            window.location.href = sharedBookingRedirect;
          }, 100);
          setShouldRender(false);
          return;
        }

        // Handle logged-in user redirects
        if (
          currentPath === "/register" ||
          currentPath === "/sign-in" ||
          currentPath === "/guest"
        ) {
          // Default redirect logic
          businessInfo
            ? navigate(`/TNPage`)
            : userInfo?.account_type === "Business"
              ? navigate(`/TNPage`)
              : currentGTP
                ? navigate(
                    `/GTP/profile/${currentGTP?.id}/talent/${currentProfile?.id}`
                  )
                : navigate(`/TNPage`);
          setShouldRender(false);
        } else if (
          userMatchedRoutePattern?.pattern?.path === userRoutePattern &&
          !["User", "Talent", "Business"].includes(userInfo?.account_type ?? "")
        ) {
          localStorage.clear();
          navigate("/home");
          setShouldRender(false);
        } else if (
          userMatchedRoutePattern?.pattern?.path === userRoutePattern &&
          userInfo?.account_type === "Talent"
        ) {
          setShouldRender(true);
        } else if (userInfo?.account_type === "Business") {
          setShouldRender(true);
        } else {
          setShouldRender(true);
        }
      } else {
        // Handle non-logged-in user redirects
        if (!allowedRoutes.includes(currentPath)) {
          console.log("it is reaching here");
          navigate("/home", {
            state: {
              redirect: location.pathname + location.search,
            },
          });
          setShouldRender(false);
        } else {
          setShouldRender(true);
        }
      }
    }, [isLoggedIn, userInfo, userLoading, navigate, location]);

    // Show loader while determining authentication state
    if (userLoading || !shouldRender) {
      return <ScreenLoader />;
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuthentication;
